import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import SENTRY from 'config/sentry.singleton';
import ELID from 'constants/elid.const';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { selectCognitoAttributeEmail } from 'utils/cognito.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import isFn from 'utils/predicates/function/isFn.util';

const IntercomWatcher = () => {
  // NOTE: must match the userId used by SEGMENT.identify (email, not Cognito UUID),
  // otherwise Intercom's Segment destination won't attach the company to the identified contact.
  const email = useSelector(selectCognitoAttributeEmail);
  const userId = fromAnyToString(email);
  const { salonId, salonName: name } = useSalonBasicInfo();

  const lastAttachment = useRef(null);

  useEffect(() => {
    if (!userId || !salonId || !name) return;
    if (!isFn(window?.analytics?.group)) return;

    const alreadyAttached = lastAttachment.current;

    // Check if already attached to same values
    if (alreadyAttached && alreadyAttached.userId === userId && alreadyAttached.salonId === salonId && alreadyAttached.name === name) {
      return; // No change, do nothing
    }

    // If re-attaching (different from last), log to your SENTRY
    if (alreadyAttached) {
      SENTRY.error({
        logger: 'IntercomWatcher',
        tags: { component: 'IntercomWatcher' },
        extra: {
          previousUserId: alreadyAttached.userId,
          previousSalonId: alreadyAttached.salonId,
          previousSalonName: alreadyAttached.name,
          currentUserId: userId,
          currentSalonId: salonId,
          currentSalonName: name,
          currentUrl: window.location.href,
          timestamp: new Date().toISOString(),
        },
        error: new Error(`IntercomWatcher: Re-attaching user`),
      });
    }

    // Now attach the new data
    window.analytics.group(salonId, { name }, { userId });

    // Save current attachment
    lastAttachment.current = { userId, salonId, name };
  }, [userId, salonId, name]);

  return <div data-bem="IntercomWatcher" id={ELID.intercomwatch} />;
};

export default IntercomWatcher;
